import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, errorMessage } from '../lib/api';
import type { OrganizationSummary } from '../lib/types';
import { PageHeader } from '../components/PageHeader';
import { useConfirm } from '../components/Confirm';
import { Field } from '../components/Field';
import { Banner } from '../components/Banner';
import { useWorkspace } from '../store/workspace';
import { isPhone } from '../lib/phone';
import { arrangeCentres, type SortKey } from '../lib/centre-list';
import { nextSort, type SortState } from '../lib/sorting';
import { SortHeader } from '../components/SortHeader';
import { Icon } from '../components/Icon';
import { ServerFiles } from '../components/ServerFiles';
import { RowMenu } from '../components/RowMenu';
import { formatDate, initials } from '../lib/when';

const SORT_LABEL: Record<SortKey, string> = {
  name: 'name',
  handle: 'handle',
  status: 'status',
  instructors: 'teachers',
  students: 'students',
  created: 'date added',
};

const slugify = (v: string) =>
  v
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);

export function OrganizationsPage() {
  const navigate = useNavigate();
  const { ask, dialog } = useConfirm();

  async function suspend(org: { id: string; name: string; isActive: boolean }) {
    setBusy(true);
    try {
      await api.patch(`/organizations/${org.id}`, { isActive: !org.isActive });
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not change that centre'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Letting a centre's candidates go back over a paper.
   *
   * The platform's decision and not the centre's, because the papers being
   * reviewed are lent to every centre: a candidate who reads the evidence
   * behind an answer can work out most of a Reading key. The server refuses
   * this from anybody else, so this button exists on no other screen.
   */
  async function setReview(org: { id: string; reviewEnabled?: boolean }) {
    setBusy(true);
    try {
      await api.patch(`/organizations/${org.id}`, { reviewEnabled: !org.reviewEnabled });
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not change review for that centre'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Deleting a centre.
   *
   * The server refuses one that holds any result, and says to suspend it
   * instead — so the dialog does not have to carry that warning, and the
   * refusal arrives as a sentence rather than as a rule nobody read. What the
   * dialog does carry is what goes with it, counted.
   */
  async function remove(org: {
    id: string;
    name: string;
    members: { instructors: number; students: number };
  }) {
    const people = org.members.instructors + org.members.students;
    const ok = await ask({
      title: `Delete ${org.name}?`,
      body: (
        <>
          <p>
            Its papers go with it, and {people === 0 ? 'nobody' : `its ${people} account${people === 1 ? '' : 's'}`}{' '}
            will be able to sign in again.
          </p>
          <p className="muted">
            A centre where anybody has sat a paper cannot be deleted — suspend that one instead, and
            the record survives.
          </p>
        </>
      ),
      confirmLabel: 'Delete the centre',
      tone: 'danger',
    });
    if (!ok) return;

    setBusy(true);
    try {
      await api.delete(`/organizations/${org.id}`);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not delete that centre'));
    } finally {
      setBusy(false);
    }
  }
  const choose = useWorkspace((w) => w.choose);
  const [orgs, setOrgs] = useState<OrganizationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortState<SortKey>>({ key: 'name', direction: 'asc' });

  const [form, setForm] = useState({
    name: '',
    slug: '',
    slugTouched: false,
    phone: '',
    fullName: '',
    password: '',
  });

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get<{ organizations: OrganizationSummary[] }>('/organizations');
      setOrgs(data.organizations);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not load organisations'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const shown = useMemo(
    () => arrangeCentres(orgs, { query, key: sort.key, direction: sort.direction }),
    [orgs, query, sort],
  );

  /**
   * Clicking a column sorts by it; clicking it again turns it around.
   *
   * A new column starts in the direction its question is usually asked —
   * biggest first for a count, newest first for a date, A to Z for a name —
   * so the first click on "Students" answers "who is the biggest?" rather
   * than needing a second click to say so.
   */
  function sortBy(key: SortKey) {
    setSort((current) => nextSort(current, key, key === 'name' || key === 'handle' ? 'asc' : 'desc'));
  }

  async function create(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/organizations', {
        name: form.name,
        slug: form.slug,
        instructor: {
          phone: form.phone,
          fullName: form.fullName,
          password: form.password,
        },
      });
      setNotice(
        `${form.name} created. The instructor signs in at /o/${form.slug} with the password you set.`,
      );
      setCreating(false);
      setForm({ name: '', slug: '', slugTouched: false, phone: '', fullName: '', password: '' });
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not create the organisation'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Organisations"
        meta={
          <p className="pagehead__lede">
            Every centre on this deployment — who teaches there, how many candidates they have, and
            what they have been allowed to switch on.
          </p>
        }
        actions={
          <button className="btn btn--primary" onClick={() => setCreating((v) => !v)}>
            {!creating && <Icon name="plus" size={16} />}
            {creating ? 'Cancel' : 'New organisation'}
          </button>
        }
      />

      <div className="stack">
        {error && <Banner tone="error">{error}</Banner>}
        {notice && <Banner tone="success">{notice}</Banner>}

        {creating && (
          <form className="panel stack rise" onSubmit={(e) => void create(e)}>
            <div>
              <h2 className="panel__title">New organisation</h2>
              <p className="muted">
                Creating a workspace also creates the instructor who owns it — the two are saved
                together, so there is never a centre nobody can sign in to.
              </p>
            </div>

            <div className="form-grid">
              <Field
                label="Centre name"
                required
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    name: e.target.value,
                    slug: f.slugTouched ? f.slug : slugify(e.target.value),
                  }))
                }
              />
              <Field
                label="Handle"
                required
                value={form.slug}
                hint="Used in the sign-in link: /o/your-handle"
                onChange={(e) =>
                  setForm((f) => ({ ...f, slug: slugify(e.target.value), slugTouched: true }))
                }
              />
            </div>

            <hr className="rule" />

            <p className="eyebrow">Owning instructor</p>
            <div className="form-grid">
              <Field
                label="Name"
                required
                value={form.fullName}
                onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
              />
              <Field
                label="Sign-in number"
                type="tel"
                inputMode="numeric"
                required
                placeholder="90 123 45 67"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                error={form.phone && !isPhone(form.phone) ? 'Use 6 to 15 digits.' : null}
                hint="What the teacher will sign in with. It need not be a real phone."
              />
              <Field
                label="Temporary password"
                type="text"
                required
                minLength={8}
                hint="At least 8 characters. Share it with the instructor to change on first use."
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              />
            </div>

            <div className="row">
              <button className="btn btn--primary" type="submit" disabled={busy}>
                {busy ? 'Creating…' : 'Create organisation'}
              </button>
            </div>
          </form>
        )}

        {!loading && orgs.length > 0 && <CentreTotals orgs={orgs} />}

        {loading ? (
          <p className="muted">Loading…</p>
        ) : orgs.length === 0 ? (
          <div className="empty">
            <p>No organisations yet.</p>
            <p className="muted">Create one to give an instructor their own branded workspace.</p>
          </div>
        ) : (
          <>
            {/* Always. A deployment with one centre still shows the box, so
                the sorting hint beside it has somewhere to live and nobody has
                to wonder whether the search exists. */}
            {(
              <div className="listbar">
                <input
                  type="search"
                  className="field__input listbar__search"
                  placeholder="Find a centre by name or handle"
                  aria-label="Find a centre by name or handle"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <p className="muted">
                  {query.trim() ? (
                    <>
                      <span className="mono">{shown.length}</span> of{' '}
                      <span className="mono">{orgs.length}</span>
                    </>
                  ) : (
                    <>
                      Sorted by <span className="mono">{SORT_LABEL[sort.key]}</span>
                    </>
                  )}
                </p>
              </div>
            )}

            {shown.length === 0 ? (
              <div className="empty">
                <p>No centre matches “{query.trim()}”.</p>
                <button className="btn" onClick={() => setQuery('')}>
                  Show all {orgs.length}
                </button>
              </div>
            ) : (
          <div className="ledger__wrap">
            <table className="ledger ledger--centres">
              <thead>
                <tr>
                  <SortHeader label="Centre" column="name" sort={sort} onSort={sortBy} />
                  <SortHeader label="Status" column="status" sort={sort} onSort={sortBy} />
                  <SortHeader label="People" column="students" sort={sort} onSort={sortBy} />
                  <th>Review</th>
                  <SortHeader label="Created" column="created" sort={sort} onSort={sortBy} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.map((org) => (
                  <tr key={org.id}>
                    {/* The centre as itself: its own colour, its name, and the
                        link its people sign in at, read as one thing. */}
                    <td>
                      <div className="org">
                        <span
                          className="org__mark"
                          aria-hidden="true"
                          style={{ '--tone': org.primaryColor ?? 'var(--brand)' } as CSSProperties}
                        >
                          {initials(org.name)}
                        </span>
                        <span className="org__text">
                          <span className="org__name">
                            {org.name}
                            {org.isPlatform && <span className="tag tag--lent">Your library</span>}
                          </span>
                          <a className="org__handle" href={`/o/${org.slug}`}>
                            /o/{org.slug}
                          </a>
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`dotstate ${org.isActive ? 'dotstate--on' : 'dotstate--off'}`}>
                        {org.isActive ? 'Active' : 'Suspended'}
                      </span>
                    </td>
                    <td className="org__people">
                      <span className="org__count">
                        <b>{org.members.instructors}</b> {org.members.instructors === 1 ? 'teacher' : 'teachers'}
                      </span>
                      <span className="org__sep" aria-hidden="true">·</span>
                      <span className="org__count">
                        <b>{org.members.students}</b> {org.members.students === 1 ? 'student' : 'students'}
                      </span>
                    </td>
                    {/* A switch rather than a button: it is a state of the
                        centre, read down the column at a glance, not an action
                        taken on it. */}
                    <td>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={Boolean(org.reviewEnabled)}
                          disabled={busy}
                          onChange={() => void setReview(org)}
                        />
                        <span className="switch__label">
                          {org.reviewEnabled ? 'On' : 'Off'}
                        </span>
                      </label>
                    </td>
                    <td className="org__date">{formatDate(org.createdAt)}</td>
                    <td>
                      <div className="org__actions">
                        <button
                          className="btn btn--sm"
                          onClick={() => {
                            choose({ id: org.id, name: org.name });
                            navigate('/tests');
                          }}
                        >
                          Open <Icon name="arrowRight" size={14} />
                        </button>
                        {/* The platform's own centre is not a customer and
                            cannot be suspended or deleted: doing either would
                            take the administrator's own library away. So it
                            has no menu, and the space is kept so the Open
                            buttons still line up. */}
                        {org.isPlatform ? (
                          <span className="org__menuSpace" aria-hidden="true" />
                        ) : (
                          <RowMenu
                            label={`More for ${org.name}`}
                            items={[
                              {
                                label: org.isActive ? 'Suspend' : 'Restore',
                                hint: org.isActive
                                  ? 'Stops everyone signing in; keeps the record'
                                  : 'Lets its people sign in again',
                                icon: org.isActive ? 'pause' : 'play',
                                disabled: busy,
                                onSelect: () => void suspend(org),
                              },
                              {
                                label: 'Delete centre',
                                hint: 'Only while nothing has been sat',
                                icon: 'trash',
                                danger: true,
                                disabled: busy,
                                onSelect: () => void remove(org),
                              },
                            ]}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
            )}
          </>
        )}

        <ServerFiles />
      </div>
      {dialog}
    </>
  );
}

/**
 * What a platform owner asks this page before anything else: how many centres,
 * how many people, and how much of it has review switched on.
 */
function CentreTotals({ orgs }: { orgs: OrganizationSummary[] }) {
  const active = orgs.filter((o) => o.isActive).length;
  const customers = orgs.filter((o) => !o.isPlatform).length;
  const teachers = orgs.reduce((n, o) => n + o.members.instructors, 0);
  const students = orgs.reduce((n, o) => n + o.members.students, 0);
  const reviewing = orgs.filter((o) => o.reviewEnabled);
  const biggest = [...orgs].sort((a, b) => b.members.students - a.members.students)[0];
  const suspended = orgs.length - active;

  return (
    <section className="totals" aria-label="At a glance">
      <div className="totals__cell">
        <p className="totals__label">Centres</p>
        <p className="totals__value">{orgs.length}</p>
        <p className="totals__note">
          {suspended === 0 ? <em>All active</em> : <>{suspended} suspended</>}
          {' · '}
          {customers} {customers === 1 ? 'customer' : 'customers'}
        </p>
      </div>
      <div className="totals__cell">
        <p className="totals__label">Teachers</p>
        <p className="totals__value">{teachers}</p>
        <p className="totals__note">
          Across {orgs.length} {orgs.length === 1 ? 'centre' : 'centres'}
        </p>
      </div>
      <div className="totals__cell">
        <p className="totals__label">Candidates</p>
        <p className="totals__value">{students}</p>
        <p className="totals__note">
          {students === 0
            ? 'None enrolled yet'
            : biggest && biggest.members.students === students
              ? `All in ${biggest.name}`
              : `Most in ${biggest?.name}`}
        </p>
      </div>
      <div className="totals__cell">
        <p className="totals__label">Review switched on</p>
        <p className="totals__value">
          {reviewing.length}
          <span className="totals__of"> / {orgs.length}</span>
        </p>
        <p className="totals__note">
          {reviewing.length === 0
            ? 'Off everywhere'
            : reviewing.length <= 2
              ? reviewing.map((o) => o.name).join(' and ')
              : `${reviewing.length} centres`}
        </p>
      </div>
    </section>
  );
}
